import { Component, Suspense, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@noissue/os-ui';

/**
 * Per-route fault isolation (Phase 1 ADR §6). Each module route is mounted inside
 * its own Suspense + error boundary, keyed on the route id and reset on
 * navigation, so a render/lifecycle/constructor throw degrades to a dead content
 * pane beside a WORKING shell rather than blanking the app.
 *
 * Note: this does NOT catch async or event-handler errors (those need the
 * module's own try/catch and TanStack Query error handling) or a runaway loop.
 * It contains the common structural failure, as the ADR specifies.
 */
class ErrorBoundaryInner extends Component<
  { children: ReactNode; onReset: () => void },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Report to Sentry as a proper exception. The boundary swallows the error, so
    // the SDK's unhandled-error capture never sees it; attach the component stack.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="p-6">
          <Card className="mx-auto max-w-lg">
            <CardHeader>
              <CardTitle>This module failed to load</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{this.state.error.message}</p>
              <Button
                onClick={() => {
                  this.setState({ error: null });
                  this.props.onReset();
                }}
              >
                Try again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

function RouteFallback() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

/** Wrap a module route. `routeKey` (the route id) resets the boundary on navigation. */
export function RouteBoundary({ routeKey, children }: { routeKey: string; children: ReactNode }) {
  return (
    <ErrorBoundaryInner key={routeKey} onReset={() => {}}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </ErrorBoundaryInner>
  );
}
